import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { default as Selector } from 'react-select';
import { FaPlus, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import moment from 'moment';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { listarProcuracoes, buscarProcuracao, criarProcuracao, editarProcuracao, inativarProcuracao, criarSubstabelecimento, uploadAnexoProcuracao, listarEstadosProcuracao } from '~/Services/Service';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ProcuracaoStats from '~/Components/Procuracoes/ProcuracaoStats';
import ProcuracaoTabela from '~/Components/Procuracoes/ProcuracaoTabela';
import ProcuracaoModal from '~/Components/Procuracoes/ProcuracaoModal';
import ProcuracaoForm from '~/Components/Procuracoes/ProcuracaoForm';
import ModalInativar from '~/Components/Procuracoes/ModalInativar';
import AuthStorage from '~/Utils/AuthStorage';

// Lookups — códigos alinhados com a API
const INSTRUMENTO_PROCURACAO = [
  { value: 1, label: 'Público' },
  { value: 2, label: 'Privado' },
  { value: 3, label: 'Certidão' },
];

const TIPO_PODER_PROCURACAO = [
  { value: 1, label: 'Aprovação' },
  { value: 2, label: 'Comercialização' },
  { value: 3, label: 'Pós-registro' },
  { value: 4, label: 'Geral/amplo' },
  { value: 5, label: 'Substabelecimento' },
  { value: 6, label: 'Misto' },
];

const SUBSTABELECIMENTO_PROCURACAO = [
  { value: 1, label: 'Vedado' },
  { value: 2, label: 'Permitido' },
  { value: 3, label: 'Reserva' },
];

// Status calculado client-side (não vem da API)
const STATUS_VIGENCIA = [
  { value: 'vigente', label: 'Vigente' },
  { value: 'alerta', label: 'Alerta (<=90d)' },
  { value: 'vencida', label: 'Vencida' },
  { value: 'indeterminada', label: 'Indeterminada' },
];

const calcularStatusVigencia = (proc) => {
  if (!proc.data_vencimento) return 'indeterminada';
  const hoje = moment();
  const venc = moment(proc.data_vencimento);
  if (venc.isBefore(hoje, 'day')) return 'vencida';
  if (venc.diff(hoje, 'days') <= 90) return 'alerta';
  return 'vigente';
};

const calcularEstatisticas = (lista) => {
  const stats = { total: lista.length, vigentes: 0, alerta: 0, vencidas: 0, indeterminada: 0 };
  lista.forEach((p) => {
    const status = calcularStatusVigencia(p);
    if (status === 'vigente') stats.vigentes++;
    else if (status === 'alerta') stats.alerta++;
    else if (status === 'vencida') stats.vencidas++;
    else stats.indeterminada++;
  });
  return stats;
};

const sortProcuracoes = (lista, ordenacao) => {
  if (!ordenacao.campo) return lista;
  const sorted = lista.slice();
  sorted.sort((a, b) => {
    const valA = a[ordenacao.campo] != null ? a[ordenacao.campo] : '';
    const valB = b[ordenacao.campo] != null ? b[ordenacao.campo] : '';
    if (valA < valB) return ordenacao.direcao === 'asc' ? -1 : 1;
    if (valA > valB) return ordenacao.direcao === 'asc' ? 1 : -1;
    return 0;
  });
  return sorted;
};

const filtrarProcuracoes = (lista, filtros) => {
  return lista.filter((p) => {
    const status = calcularStatusVigencia(p);

    if (filtros.busca) {
      const termo = filtros.busca.toLowerCase();
      const campos = [
        p.descricao_tipo_instrumento,
        p.descricao_tipo_poder,
        p.descricao_tipo_substabelecimento,
        p.tabeliao_nome,
        p.estados_validade,
        p.observacao,
        p.nome_usuario_criador,
        p.outorgante_resumo,
        p.outorgado_resumo,
      ];
      const match = campos.some((c) => c && c.toLowerCase().indexOf(termo) >= 0);
      if (!match) return false;
    }

    if (filtros.instrumento && p.codigo_tipo_instrumento !== filtros.instrumento) return false;
    if (filtros.tipo && p.codigo_tipo_poder !== filtros.tipo) return false;
    if (filtros.status && status !== filtros.status) return false;

    return true;
  });
};

export default function Procuracoes() {
  const [procuracoes, setProcuracoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    busca: '',
    instrumento: '',
    tipo: '',
    status: '',
  });
  const filtrosVazios = { busca: '', instrumento: '', tipo: '', status: '' };
  const [ordenacao, setOrdenacao] = useState({ campo: 'data_lavratura', direcao: 'desc' });
  const [modalDetalhe, setModalDetalhe] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [procuracaoEdicao, setProcuracaoEdicao] = useState(null);
  const [inativarCodigo, setInativarCodigo] = useState(null);
  const [substabelecimentoOrigem, setSubstabelecimentoOrigem] = useState(null);
  const [optionsEstados, setOptionsEstados] = useState([]);

  const temPermissaoCadastro = AuthStorage.temCaracteristicas([627]);
  const temPermissaoEdicao = AuthStorage.temCaracteristicas([627]) && AuthStorage.temCaracteristicas([628]);

  const [filtroBusca, setFiltroBusca] = useState('');
  const [filtroInstrumento, setFiltroInstrumento] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState(null);

  const carregarLista = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarProcuracoes();
      const lista = (Array.isArray(data) ? data : []).map((p) => ({
        ...p,
        status_vigencia: calcularStatusVigencia(p),
      }));
      setProcuracoes(lista);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao carregar procurações';
      toast.error(msg);
      setProcuracoes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarLista();
    listarEstadosProcuracao()
      .then((data) => {
        setOptionsEstados((data || []).map((uf) => ({ value: uf, label: uf })));
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBuscar = useCallback(() => {
    setFiltros({
      busca: filtroBusca,
      instrumento: filtroInstrumento ? filtroInstrumento.value : '',
      tipo: filtroTipo ? filtroTipo.value : '',
      status: filtroStatus ? filtroStatus.value : '',
    });
  }, [filtroBusca, filtroInstrumento, filtroTipo, filtroStatus]);

  const handleLimpar = useCallback(() => {
    setFiltroBusca('');
    setFiltroInstrumento(null);
    setFiltroTipo(null);
    setFiltroStatus(null);
    setFiltros(filtrosVazios);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carregarDetalhe = useCallback(async (codigo) => {
    setModalDetalhe(codigo);
    setLoadingDetalhe(true);
    setDetalhe(null);
    try {
      const data = await buscarProcuracao(codigo);
      setDetalhe(data);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao carregar detalhe da procuração';
      toast.error(msg);
      setModalDetalhe(null);
    } finally {
      setLoadingDetalhe(false);
    }
  }, []);

  const handleDetalhe = useCallback((codigo) => {
    carregarDetalhe(codigo);
  }, [carregarDetalhe]);

  const handleCloseModal = useCallback(() => {
    setModalDetalhe(null);
    setDetalhe(null);
  }, []);

  const handleNavegar = useCallback((codigo) => {
    carregarDetalhe(codigo);
  }, [carregarDetalhe]);

  const handleInativar = useCallback((codigo) => {
    setInativarCodigo(codigo);
    setModalDetalhe(null);
    setDetalhe(null);
  }, []);

  const handleConfirmarInativar = useCallback(async (codigo, justificativa) => {
    try {
      await inativarProcuracao(codigo, { justificativa });
      toast.success('Procuração inativada com sucesso!');
      setInativarCodigo(null);
      carregarLista();
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao inativar procuração';
      toast.error(msg);
    }
  }, [carregarLista]);

  const handleEditar = useCallback(async (codigo) => {
    try {
      const data = await buscarProcuracao(codigo);
      setProcuracaoEdicao(data);
      setModalDetalhe(null);
      setDetalhe(null);
      setShowFormModal(true);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao carregar procuração para edição';
      toast.error(msg);
    }
  }, []);

  const handleCriarSubstabelecimento = useCallback((codigoOrigem) => {
    setSubstabelecimentoOrigem(codigoOrigem);
    setModalDetalhe(null);
    setDetalhe(null);
    setShowFormModal(true);
  }, []);

  const uploadAnexosSequencial = async (codigoProcuracao, arquivos) => {
    for (let i = 0; i < arquivos.length; i++) {
      const formData = new FormData();
      formData.append('arquivo', arquivos[i]);
      await uploadAnexoProcuracao(codigoProcuracao, formData);
    }
  };

  const handleSalvar = useCallback(async (payload, codigoEdicao, anexos) => {
    try {
      let codigoFinal = codigoEdicao;

      if (codigoEdicao) {
        await editarProcuracao(codigoEdicao, payload);
      } else {
        const resultado = await criarProcuracao(payload);
        codigoFinal = resultado ? resultado.codigo : null;

        // Se foi criada como substabelecimento, vincular automaticamente
        if (substabelecimentoOrigem && codigoFinal) {
          await criarSubstabelecimento(substabelecimentoOrigem, {
            codigo_procuracao_destino: codigoFinal,
            com_reserva: false,
          });
        }
      }

      // Upload de anexos sequencial
      if (anexos && anexos.length > 0 && codigoFinal) {
        await uploadAnexosSequencial(codigoFinal, anexos);
      }

      // Toast de sucesso
      if (codigoEdicao) {
        toast.success('Procuração atualizada com sucesso!');
      } else if (substabelecimentoOrigem) {
        toast.success('Procuração criada e vinculada como substabelecimento!');
      } else {
        toast.success('Procuração criada com sucesso!');
      }

      setShowFormModal(false);
      setProcuracaoEdicao(null);
      setSubstabelecimentoOrigem(null);
      carregarLista();
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao salvar procuração';
      toast.error(msg);
    }
  }, [carregarLista, substabelecimentoOrigem]);

  const procuracoesFiltradas = filtrarProcuracoes(procuracoes, filtros);
  const procuracoesSorted = sortProcuracoes(procuracoesFiltradas, ordenacao);
  const estatisticas = calcularEstatisticas(procuracoesFiltradas);

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Procurações" />

      <div className="mt-3" />

      <ProcuracaoStats estatisticas={estatisticas} loading={loading} />

      <Card className="shadow-sm mb-3">
        <Card.Body className="px-2 px-sm-3">
          <Row className="align-items-end">
            <Col xs={12} sm={6} md className="mb-2">
              <Form.Label className="small mb-1">Busca</Form.Label>
              <Form.Control
                type="text"
                placeholder="Outorgante, outorgado, tabeliao..."
                value={filtroBusca}
                onChange={(e) => { setFiltroBusca(e.target.value); }}
                style={{ height: 42 }}
              />
            </Col>
            <Col xs={12} sm={6} md className="mb-2">
              <Form.Label className="small mb-1">Instrumento</Form.Label>
              <Selector
                styles={customStyles}
                components={{ DropdownIndicator }}
                placeholder="Todos"
                options={INSTRUMENTO_PROCURACAO}
                isClearable
                value={filtroInstrumento}
                onChange={setFiltroInstrumento}
                menuPortalTarget={document.body}
                noOptionsMessage={() => 'Nenhuma opção'}
              />
            </Col>
            <Col xs={12} sm={6} md className="mb-2">
              <Form.Label className="small mb-1">Tipo</Form.Label>
              <Selector
                styles={customStyles}
                components={{ DropdownIndicator }}
                placeholder="Todos"
                options={TIPO_PODER_PROCURACAO}
                isClearable
                value={filtroTipo}
                onChange={setFiltroTipo}
                menuPortalTarget={document.body}
                noOptionsMessage={() => 'Nenhuma opção'}
              />
            </Col>
            <Col xs={12} sm={6} md className="mb-2">
              <Form.Label className="small mb-1">Status</Form.Label>
              <Selector
                styles={customStyles}
                components={{ DropdownIndicator }}
                placeholder="Todos"
                options={STATUS_VIGENCIA}
                isClearable
                value={filtroStatus}
                onChange={setFiltroStatus}
                menuPortalTarget={document.body}
                noOptionsMessage={() => 'Nenhuma opção'}
              />
            </Col>
          </Row>
          <Row className="align-items-center mt-2">
            <Col xs={12} sm={"auto"} className="ml-sm-auto">
              <div className="d-flex">
                {temPermissaoCadastro && (
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => { setShowFormModal(true); }}
                    className="mr-2"
                  >
                    <FaPlus className="mr-1" />
                    Nova
                  </Button>
                )}
                <Button variant="outline-secondary" size="sm" onClick={handleLimpar} className="mr-2">
                  Limpar
                </Button>
                <Button variant="primary" size="sm" onClick={handleBuscar} disabled={loading}>
                  {loading ? (
                    <Spinner as="span" animation="border" size="sm" />
                  ) : (
                    <>
                      <FaSearch className="mr-1" /> Buscar
                    </>
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <Loading label="Carregando procurações..." />
      ) : (
        <ProcuracaoTabela
          procuracoes={procuracoesSorted}
          ordenacao={ordenacao}
          onOrdenacao={setOrdenacao}
          onDetalhe={handleDetalhe}
        />
      )}

      <ProcuracaoForm
        show={showFormModal}
        onSalvar={handleSalvar}
        onCancelar={() => { setShowFormModal(false); setProcuracaoEdicao(null); setSubstabelecimentoOrigem(null); }}
        procuracaoEdicao={procuracaoEdicao}
        optionsInstrumento={INSTRUMENTO_PROCURACAO}
        optionsTipoPoder={TIPO_PODER_PROCURACAO}
        optionsSubstabelecimento={SUBSTABELECIMENTO_PROCURACAO}
        optionsEstados={optionsEstados}
      />

      {modalDetalhe != null && (
        <ProcuracaoModal
          detalhe={detalhe}
          loading={loadingDetalhe}
          onClose={handleCloseModal}
          onNavegar={handleNavegar}
          onEditar={temPermissaoEdicao ? handleEditar : null}
          onInativar={temPermissaoEdicao ? handleInativar : null}
          onCriarSubstabelecimento={temPermissaoCadastro ? handleCriarSubstabelecimento : null}
        />
      )}

      <ModalInativar
        show={inativarCodigo != null}
        codigo={inativarCodigo}
        onConfirmar={handleConfirmarInativar}
        onCancelar={() => { setInativarCodigo(null); }}
      />
    </Container>
  );
}
